import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { ComingSoonBadge } from '@/components/common/ComingSoonBadge';
import { useAuth } from '@/hooks/useAuth';
import { useIssueReportOffer } from '@/hooks/useIssueReportOffer';
import {
  ISSUE_REPORT_OFFER_STATE,
  ISSUE_REPORT_OFFER_STRINGS,
  JOURNEY_ROUTE,
  OFFER_CTA,
  PURCHASE_INTENT,
} from '@/lib/constants/issue-report';
import { CtaLink } from './CtaLink';
import { IssueReportPathDialog } from './IssueReportPathDialog';
import { storePurchaseIntent } from '@/lib/auth/intent-storage';

/** The CMS flag that ties a CTA to the Issue Report offer (FR-202). Absent for every other CTA. */
export type SectionOfferCta = (typeof OFFER_CTA)[keyof typeof OFFER_CTA];

export interface SectionCtaProps {
  label: string;
  /** Absent for an inert CTA (a button with no destination). */
  url?: string;
  offerCta?: SectionOfferCta;
  /**
   * Opens this CTA in a new browser tab (MYN-301, FR-217). Absent for every
   * CTA except the Due Diligence page's two scheduler links - every other
   * CTA keeps today's same-tab navigation.
   */
  openInNewTab?: boolean;
}

/**
 * A section's call to action (card-grid and comparison-split). Normally a
 * routed button, or an inert one when the CMS gives no destination. A CTA
 * flagged `offerCta: 'issue-report'` follows the public offer (FR-202):
 *
 * - `coming_soon`: a non-clickable "Coming Soon" in its place;
 * - still loading: nothing, so the buy button never flashes and then turns
 *   into "Coming Soon";
 * - available, or the request failed: the normal button. Its destination is
 *   the journey route (FR-204), which handles a failed offer with its own
 *   retry, so a visitor is never left without a way in. A signed-out
 *   visitor sees "Log in or create an account to get started." under it, and
 *   the click first shows the path they are about to follow (FR-223).
 */
export function SectionCta({ label, url, offerCta, openInNewTab }: SectionCtaProps) {
  const isOfferCta = offerCta === OFFER_CTA.ISSUE_REPORT;
  const { data: offer, isPending } = useIssueReportOffer({ enabled: isOfferCta });

  if (isOfferCta) {
    if (offer?.state === ISSUE_REPORT_OFFER_STATE.COMING_SOON) return <ComingSoonBadge />;
    if (isPending) return null;
    if (url) return <IssueReportCta label={label} url={url} />;
  }

  return url ? (
    <Button asChild variant="primary" className="w-fit">
      <CtaLink to={url} {...(openInNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
        {label}
      </CtaLink>
    </Button>
  ) : (
    // Inert CTA (docs/specs/features/public-pages/02-design.md §23, A-133): a
    // real, focusable <button> with no handler, not a disabled treatment.
    <Button type="button" variant="primary" className="w-fit">
      {label}
    </Button>
  );
}

/**
 * The Issue Report CTA once it can be bought. Its own component so only this
 * CTA reads the session: signed in, the plain link into the journey; signed
 * out, the path pop-up first and the sign-in line under the button (FR-223).
 */
function IssueReportCta({ label, url }: { label: string; url: string }) {
  const { isAuthenticated } = useAuth();
  const [pathOpen, setPathOpen] = useState(false);

  if (isAuthenticated) {
    // Already signed in: the journey follows the login route's steps, whatever an earlier visit stored.
    return (
      <Button asChild variant="primary" className="w-fit">
        <CtaLink
          to={url}
          onClick={() => storePurchaseIntent(PURCHASE_INTENT.ISSUE_REPORT, Date.now(), JOURNEY_ROUTE.LOGIN)}
        >
          {label}
        </CtaLink>
      </Button>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      <Button type="button" variant="primary" className="w-fit" onClick={() => setPathOpen(true)}>
        {label}
      </Button>
      <p className="text-subtext m-0 text-sm">{ISSUE_REPORT_OFFER_STRINGS.SIGN_IN_LINE}</p>
      <IssueReportPathDialog open={pathOpen} onOpenChange={setPathOpen} />
    </div>
  );
}

